'use client';

import { FC, useState } from 'react';
import { Content } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { ChevronIcon } from '@/assets';

// Arrow icon for standard variation
const ArrowIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M9 6L15 12L9 18"
      stroke="#05F273"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Props for `FaqAccordion`.
 */
export type FaqAccordionProps = SliceComponentProps<Content.FaqAccordionSlice>;

/**
 * Component for "FaqAccordion" Slices.
 */
const FaqAccordion: FC<FaqAccordionProps> = ({ slice }) => {
  const [openItems, setOpenItems] = useState<number[]>([0]); // First item (index 0) is open by default

  const toggleItem = (index: number) => {
    setOpenItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  // Standard variation - centered title with full-width cards
  if (slice.variation === 'standard') {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative bg-[#0B2B26] py-20 px-4 md:px-8 lg:px-[150px]"
      >
        <div className="max-w-[1140px] mx-auto">
          <div className="flex flex-col gap-[60px] items-center w-full">
            {/* Centered Title */}
            {slice.primary.section_title && (
              <h2 className="text-white font-medium text-[32px] md:text-[42px] leading-[1.2] text-center">
                <PrismicRichText
                  field={slice.primary.section_title}
                  components={{
                    heading2: ({ children }) => <span>{children}</span>,
                    paragraph: ({ children }) => <span>{children}</span>,
                    strong: ({ children }) => <strong>{children}</strong>,
                    em: ({ children }) => <em>{children}</em>,
                  }}
                />
              </h2>
            )}

            {/* FAQ Cards - Full Width */}
            <div className="flex flex-col gap-6 w-full">
              {slice.primary.faq_items.map((item, index) => (
                <div
                  key={index}
                  className="backdrop-blur-[10px] bg-gradient-to-b from-[rgba(0,0,0,0.4)] to-transparent p-6 rounded-2xl shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] overflow-hidden"
                >
                  {/* Question Header - Clickable */}
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full flex items-start justify-between gap-6 text-left focus:outline-none"
                  >
                    <h3 className="flex-1 text-[#05F273] text-xl md:text-[24px] font-bold leading-[1.4]">
                      <PrismicRichText
                        field={item.question}
                        components={{
                          heading3: ({ children }) => <span>{children}</span>,
                          paragraph: ({ children }) => <span>{children}</span>,
                          strong: ({ children }) => <strong>{children}</strong>,
                          em: ({ children }) => <em>{children}</em>,
                        }}
                      />
                    </h3>
                    <div
                      className={`flex-shrink-0 transform transition-transform duration-300 ${
                        openItems.includes(index) ? 'rotate-90' : ''
                      }`}
                    >
                      <ArrowIcon />
                    </div>
                  </button>

                  {/* Answer Content - Collapsible */}
                  <div
                    className={`transition-all duration-300 ease-in-out ${
                      openItems.includes(index)
                        ? 'max-h-[500px] opacity-100 mt-6'
                        : 'max-h-0 opacity-0 mt-0'
                    } overflow-hidden`}
                  >
                    <div className="text-white text-base md:text-[18px] leading-[1.4]">
                      <PrismicRichText
                        field={item.answer}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="mb-4 last:mb-0">{children}</p>
                          ),
                          strong: ({ children }) => <strong>{children}</strong>,
                          em: ({ children }) => <em>{children}</em>,
                          hyperlink: ({ children, node }) => (
                            <a
                              href={node.data.url}
                              className="text-[#05F273] underline hover:no-underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default variation - title on left, cards on right
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pt-20 pb-16 px-4 md:px-8 lg:px-[150px]"
      style={{ background: '#0E1110' }}
    >
      {/* Container with same margins as other sections */}
      <div className="max-w-[1440px] mx-auto">
        {/* Flex layout: Title on left, Cards on right */}
        <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-16">
          {/* Section Title - Left side */}
          {slice.primary.section_title && (
            <div className="lg:w-1/3 flex-shrink-0">
              <h2 className="text-white font-bold text-3xl lg:text-5xl leading-tight">
                <PrismicRichText
                  field={slice.primary.section_title}
                  components={{
                    heading2: ({ children }) => <span>{children}</span>,
                    paragraph: ({ children }) => <span>{children}</span>,
                    strong: ({ children }) => <strong>{children}</strong>,
                    em: ({ children }) => <em>{children}</em>,
                  }}
                />
              </h2>
            </div>
          )}

          {/* FAQ Cards - Right side */}
          <div className="lg:w-2/3 flex-1 space-y-4">
            {slice.primary.faq_items.map((item, index) => (
              <div
                key={index}
                className="overflow-hidden"
                style={{
                  borderRadius: '16px',
                  background:
                    'linear-gradient(180deg, rgba(2, 104, 70, 0.40) 0%, rgba(0, 0, 0, 0.00) 100%)',
                  boxShadow: '0 4.325px 64.871px 0 rgba(0, 0, 0, 0.08)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {/* Question Header - Clickable */}
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-6 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                >
                  <div className="flex-1 pr-4">
                    <h3
                      className="font-semibold text-lg"
                      style={{ color: '#05F273' }}
                    >
                      <PrismicRichText
                        field={item.question}
                        components={{
                          heading3: ({ children }) => <span>{children}</span>,
                          paragraph: ({ children }) => <span>{children}</span>,
                          strong: ({ children }) => <strong>{children}</strong>,
                          em: ({ children }) => <em>{children}</em>,
                        }}
                      />
                    </h3>
                  </div>
                  <div
                    className={`transform transition-transform duration-300 ${
                      openItems.includes(index) ? 'rotate-180' : ''
                    }`}
                  >
                    <ChevronIcon width={24} height={24} fill="#05F273" />
                  </div>
                </button>

                {/* Answer Content - Collapsible */}
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    openItems.includes(index)
                      ? 'max-h-96 opacity-100'
                      : 'max-h-0 opacity-0'
                  } overflow-hidden`}
                >
                  <div className="px-6 pb-6">
                    <div className="text-white/90 text-base leading-relaxed">
                      <PrismicRichText
                        field={item.answer}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="mb-4 last:mb-0">{children}</p>
                          ),
                          strong: ({ children }) => <strong>{children}</strong>,
                          em: ({ children }) => <em>{children}</em>,
                          hyperlink: ({ children, node }) => (
                            <a
                              href={node.data.url}
                              className="text-green-400 underline hover:no-underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {children}
                            </a>
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqAccordion;
